import { useMarkAllFollowersRead } from "@/src/features/following/hooks";
import { FollowerInbox } from "@/src/features/following/components";
import Notifications from "@/src/components/settings/notifications/notifications";
import { useMarkAllNotificationAsRead } from "@/src/services/api/notification/notification.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

const NOTIFICATION_TABS = ["LABEL_ALL", "LABEL_UNREAD"] as const;
type NotificationTab = (typeof NOTIFICATION_TABS)[number];

const NotificationScreen = () => {
  const { t } = useTranslation();
  const { varStyle } = useTheme();
  const markFollowers = useMarkAllFollowersRead();
  // Unread is the landing tab: the inbox is opened to deal with what is new,
  // and the nav badge that brings the user here counts unread rows only — so
  // "All" as the default showed a list whose top rows were already handled.
  // "All" is one tap away for history.
  const [activeTab, setActiveTab] = useState<NotificationTab>("LABEL_UNREAD");
  const { mutate: markAllAsRead, isPending: isMarkingAll } =
    useMarkAllNotificationAsRead();

  return (
    <View style={varStyle} className="flex-1 bg-obsidian gap-4">
      <ScrollView
        contentContainerClassName="gap-4"
        showsVerticalScrollIndicator={false}
      >
        {/* ── Header card ── */}
        <View className="bg-chalk px-4 pt-4 md:px-6 rounded-2xl md:rounded-[16px] md:border md:border-ash-3">
          <View className="flex-row items-center justify-between">
            <Text className="text-base font-bold text-onyx">
              {t("LABEL_NOTIFICATION")}
            </Text>
            <TouchableOpacity
              onPress={() => {
                markAllAsRead();
                markFollowers.mutate();
              }}
              disabled={isMarkingAll || markFollowers.isPending}
              className="px-4 py-1.5 rounded-full bg-ash md:border md:border-ash-3 hover:bg-ash-3"
            >
              {isMarkingAll || markFollowers.isPending ? (
                <ActivityIndicator size="small" />
              ) : (
                <Text className="text-xs text-primary font-medium">
                  {t("BTN_MARK_ALL_AS_READ")}
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {markFollowers.isError && (
            <Text
              accessibilityLiveRegion="polite"
              className="text-crimson text-sm"
            >
              {t("FOLLOW_RECONCILE")}
            </Text>
          )}
          {/* Tab strip with padding below */}
          <View className="pt-3 pb-3">
            <View className="flex-row gap-1">
              {NOTIFICATION_TABS.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <Pressable
                    key={tab}
                    onPress={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-t-xl ${isActive ? "bg-primary-2 border-b-2 border-primary hover:bg-primary-2" : "hover:bg-ash-3"}`}
                  >
                    <Text
                      className={`text-sm font-medium ${isActive ? "text-primary" : "text-ash-1"}`}
                    >
                      {t(tab)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>

        {/* ── Content card ── */}
        <View className="flex-1 bg-chalk rounded-2xl md:rounded-[16px] px-2 py-2 md:p-4 md:border md:border-ash-3">
          <FollowerInbox unreadOnly={activeTab === "LABEL_UNREAD"} />
          <Notifications isUnreadOnly={activeTab === "LABEL_UNREAD"} />
        </View>
      </ScrollView>
    </View>
  );
};

export default NotificationScreen;
